<template>
<div class="chapter">
<div class="inner_page">
<div class="chapter" id="sec-23">
<h1 class="chapter" id="pz23-1" style="text-align: center;">
					Introduction </h1>
<br/>
<h1 id="pz23-2">
					TELLING OUR STORIES </h1>
<p id="pz23-3" style="text-indent: 0">
<i>Whoever speaks must do so as one speaking the very words of God; whoever serves
					must do so with the strength that God supplies, so that God may be glorified in all
					things through Jesus Christ. To him belong the glory and the power forever and ever.
					Amen.</i> (1 Pet. 4:11 <span class="small">NRSV</span>) </p>
<br/>
<br/>
<p id="pz23-4" style="text-indent: 0">
					It began with a small group of women gathered around a table in my office during
					the 2000 Pepperdine University Bible Lectures. Given the history of male leadership
					in the Churches of Christ, we were discussing ways we could provide support and blessing
					for women who were in graduate programs in theology or ministry, or already in ministerial
					positions. Because of the autonomous nature of separate congregations in the Churches
					of Christ, our greatest challenge was identifying those who would be interested
					in such a network of support. With the help of Amy Bost Henegar, we created a listserv,
					beginning with the names of women we knew. That was the start of a network of women
					in ministry in the Churches of Christ, which proved to be a great resource as we
					moved forward. </p>
<p id="pz23-5">
					When I became the chaplain at Pepperdine University, I was appointed to a steering
					committee charged with writing a grant proposal for the Lilly Endowment that focused
					on a theological understanding of vocation. The endowment required that part of the
					grant be directed toward developing greater student interest in congregational ministry.
					It was a perfect fit for women in the Churches of Christ pursuing the call to ministry,
					so I proposed using the funding for a national conference. We received the grant,
					<span id="z24-1">and on May 1–3, 2005, Pepperdine University hosted the first annual conference for
					women in ministry in the Churches of Christ. I would have been delighted if twenty-five
					women had come, but forty-two participated in the conference. Among the participants
					were youth and children’s ministers, university and hospital chaplains, campus ministers,
					spiritual directors, professors of religion, and graduate students. </span></p>
<p id="pz24-2">
					Kathy Pulley, professor of religious studies at Missouri State University, was the
					keynote speaker and presented a history of female preachers in the early years of
					our religious heritage—the Stone-Campbell Movement. I spoke with Kathy following
					her presentation, and she observed that while we know the names of these women and
					quite a lot about their lives, none of their sermons—their words—were preserved.
					Therefore, she encouraged me to put my sermons in manuscript form and document them
					for historical purposes. We sensed that a new history of women in the Churches of
					Christ was in the making. Accordingly, I wrote and preserved the two sermons I had
					preached, with a hopeful heart there would be more. </p>
<p id="pz24-3">
					With the national conference at Pepperdine, Women in Ministry<sup><a href="#/#pz28-8" id="pz28-8a">1</a></sup> was born; its network,
					subsequent meetings, and annual conferences provide ample evidence of what is happening
					among women in our churches and church-related universities, although the local autonomy
					among the Churches of Christ makes it difficult to track information with complete
					accuracy. Nevertheless, I began encountering women who were finding opportunities
					to preach, so I started collecting their sermons, and a history began to emerge.
					That marked the beginnings of this book. <i>Finding Their Voices</i> seeks to honor women
					from the Churches of Christ who, in the late twentieth and early twenty-first centuries,
					have answered the call to preaching and other forms of ministry. The book also seeks
					to recognize the churches and institutions that have welcomed these women into their
					pulpits, providing space for their voices to be heard. </p>
<p id="pz24-4">
					As far as we know, Kathy Pulley was the first woman of my generation to preach in
					a Church of Christ when, in 1984, she spoke at the Brookline Church of Christ in
					Brookline, Massachusetts. Her sermon, “Living in the Kingdom,” opens this collection.
					Interestingly, my first sermon, given <span id="z25-1">with my husband, Stuart Love, was also at the
					Brookline church in 1992. However, my first “solo” sermon was preached ten years
					later, in 2002, at the Jewish synagogue in Malibu, California, as part of the Malibu
					Community Thanksgiving Service. I wondered then if I would ever have a similar invitation
					to preach for a Church of Christ. That invitation came in 2011 from the Manhattan
					Church of Christ in Manhattan, New York. I had waited almost a lifetime to preach
					for a Church of Christ—I was seventy years old. </span></p>
<p id="pz25-2">
					As my story illustrates, change takes place slowly in the Churches of Christ, in
					large part because of the lack of a central organization. Change takes place one
					congregation at a time. Although the opportunities for women to preach in Churches
					of Christ are rare, they are increasing, and this book is an attempt to document
					that history as well as preserve their words. In the early stages of this project,
					I collected ten sermons and published them in a 2008 issue of <i>Leaven</i>, a journal
					of Christian ministry for churches of the Restoration Heritage.<sup><a href="#/#pz28-9" id="pz28-9a">2</a></sup> Of those ten, only
					four were preached in Churches of Christ. Others were preached in Disciples of Christ
					congregations and in university settings. Three were presented in graduate school
					chapels or preaching classes. I reasoned that if these women were given the opportunity
					to speak the words, then they should be preserved, regardless of where they were
					spoken. This is part of our story. Each sermon preached, whether in chapel or in
					a class, is a boundary crossed. In the years that followed, changes began to take
					place, and now some of these same voices can be heard preaching in Churches of Christ.
					Nevertheless, these opportunities remain rare, the occasions infrequent. </p>
<p id="pz25-3">
					To underscore the historical significance of this book, the sermons are arranged
					in chronological order<i>.</i> Also, each sermon is documented with the date, place, and
					occasion when it was preached. Only four of the sermons are from the twentieth century
					(1984–1992). These four were preached in Churches of Christ in Brookline, Massachusetts,
					and Flagstaff, Arizona. Kathy Pulley and her sister, Micki Pulleyking, each contributed
					a sermon preached at the Brookline church. Micki’s sermon, “What Will We Do in Boston?,”
					was preached in 1986, while she served as the co-minister for the Brookline Church
					of Christ. Elsie Eyre and Carolyn Hunter’s sermons at <span id="z26-1">the Flagstaff church were delivered
					in 1991 and 1992. Thanks to Carolyn, we are able to document this little-known bit
					of history and preserve two of the sermons delivered by women at that congregation.
					Katie Hays’s sermon, “Mark 4:35–41—A Text for the Sunday after 9/11,” marks the first
					entry of the twenty-first century. It was preached on September 14, 2001, in West
					Islip, New York—the Sunday after September 11. At the time, she was the full-time
					pulpit minister for the West Islip Church of Christ. </span></p>
<p id="pz26-2">
					Micki and Katie are important parts of the story as they represent women in Churches
					of Christ who are well-trained and gifted preachers. While they believed they were
					seeing the beginnings of change, they also believed, as Micki states in the preface
					to her sermon, that opportunities in Churches of Christ were not forthcoming: “I
					would grow old waiting. . . . So after years of hope, mixed with sorrow, I accepted
					the call to serve a Disciples congregation.” Both Micki and Katie were ordained to
					Christian ministry in the Christian Church (Disciples of Christ). Similarly, Charme
					Robarts, the first woman to preach at the Bering Drive Church of Christ in Houston,
					Texas, in 2006, now attends and often preaches for a United Methodist Church in Fort
					Worth, Texas. Their stories are counterbalanced by those who have remained in Churches
					of Christ and those who have fulfilled their call to ministry elsewhere. Their examples
					inspire and encourage women who are struggling to find a place where they can pursue
					the call to preach. </p>
<p id="pz26-3">
					Of the twenty-nine sermons in this collection, twenty-two were preached in Churches
					of Christ, which indicates a developing story. Many of them document the first sermon
					preached by a woman in that church or the first sermon that particular woman has
					preached anywhere, whether inside or outside the Churches of Christ. Several of the
					sermons printed in the 2008 <i>Leaven</i> issue appear in this book, and several of those
					contributors submitted additional sermons for inclusion in this volume that were
					preached in Churches of Christ. For example, Jennifer Christy’s <i>Leaven</i> sermon, “Trust
					in the Lord,” was preached in graduate chapel at Abilene Christian University in
					2006. She subsequently preached in several settings, including in a Christian Church
					(Disciples of Christ). In 2013, she <span id="z27-1">was privileged to be the first woman to preach
					for her home congregation, the Camarillo Church of Christ in Camarillo, California.
					Her sermon, “Friends Don’t Let Friends . . . ,” appears in this book. In January
					2015, Jennifer became the associate minister for the Westside Church of Christ in
					Portland, Oregon. Jen’s story reflects the journey of several women in Churches of
					Christ and carries the promise of the corporate story recorded in this volume. It
					also speaks hope for the future. </span></p>
<p id="pz27-2">
					It is not unusual for women in Churches of Christ to preach in Disciples of Christ
					or other churches before receiving invitations to preach for a Church of Christ.
					Christian colleges and universities also provide opportunities for women. Rochester
					College, Abilene Christian University, Lipscomb University, and Pepperdine University,
					for example, are intentional about inviting women to preach for graduate chapel,
					baccalaureate, Bible lectures, sermon seminars, and conferences, as well as for Thanksgiving,
					Advent, and Easter services. Such occasions have contributed greatly to the advancement
					of opportunities for women, especially in the past five years. Beth Bowers’s sermon,
					“Such as These,” was preached in a graduate preaching class at Rochester College
					in 2006. She represents women with theological training who have had no opportunity
					to preach beyond the classroom. </p>
<p id="pz27-3">
					The prefaces before each sermon provide brief glimpses into each woman’s story.
					They are stories of faith, persistence, courage, and waiting. For example, Natalie
					Magnusson shares her experience of writing sermons with no expectation that she would
					ever be able to preach them. That changed after she shared one with a friend—a dean
					at Rochester College. Convinced that her sermon needed to be preached, the dean planned
					an Advent service at the college and invited Natalie to be the preacher. These prefaces
					demonstrate the movement toward gender inclusion in our congregations, colleges,
					and universities that provide welcoming environments vital for nurturing the gifts
					of women. </p>
<p id="pz27-4">
					The biographies in this book mark the ways in which some of these women are pursuing
					professional ministry in churches, hospitals, and universities. Others make vital
					contributions to their local churches by using their leadership gifts in worship,
					teaching, and counseling. As far as <span id="z28-1">we know, Ann Bayliss is presently the only full-time
					pulpit minister in a Church of Christ. After preaching for several years as the associate
					minister of the Heights Church of Christ in Houston, Texas, she was appointed the
					preaching minister in 2014. Her sermon in this collection, “Lamentations, the Lost
					Language of the Church,” was preached in 2013. </span></p>
<p id="pz28-2">
					The contributors to <i>Finding Their Voices</i> are well-educated, most having earned graduate
					degrees in ministry, theology, or related areas. There are other women in the Churches
					of Christ who are equally prepared and called, but they have not yet found places
					for their homiletic gifts to be used. Tiffany Dahlman, who is not included in this
					book, expresses well the unfulfilled desire of many who know this reality. She writes,
					“I am writing a series of sermons now that will go into a quiet folder with other
					sermons on diversity in the church, mercy, the Gospel of Mark, and the ‘I am’ statements
					in John’s gospel. There is nothing more depressing than pouring one’s heart out in
					a sermon that may never be preached.” </p>
<p id="pz28-5">
					Other women and churches are part of the story revealed in this book. The intent
					is not to produce an exhaustive history, but to document a developing, unfinished
					narrative, bringing attention to the fact that more and more gifted women are embracing
					their particular calling to preach. Our hope is that <i>Finding Their Voices</i> will encourage
					women to develop and use their gifts with boldness. We also hope to encourage churches
					and church leaders to embrace the gifts of women in their midst and to provide opportunities
					for their voices to be heard. </p>
<br/>
<p class="center" id="pz28-7" style="font-size:1.1em;">
					Notes </p>
<p id="pz28-8">
<sup><a href="#/#pz28-8a">1</a></sup> Women in Ministry exists to provide blessing, encouragement, and support to women
						who are called to ministry and church leadership in Churches of Christ. The Women
						in Ministry website is <a href="http://www.womeninministrycc.com" id="AJyD" target="_blank">www.womeninministrycc.com</a>. If you would like to be added to
						the WIM Facebook discussion page, you may do so by sending your name, email address,
						and a brief description of your ministry to one of the following: Amy Bost Henegar
						(<a href="mailto:amyebh@gmail.com" id="AQEM">amyebh@gmail.com</a>), D’Esta Love (<a href="mailto:dlove@pepperdine.edu" id="A9Yp">dlove@pepperdine.edu</a>), or Sara Barton (<a href="mailto:sara.barton@pepperdine.edu" id="A9ca">sara.barton@pepperdine.edu</a>).
						</p>
<p id="pz28-9">
<sup><a href="#/#pz28-9a">2</a></sup> D’Esta Love and Stuart Love, eds., “Your Sons and Your Daughters Shall Prophesy,”
						special issue, <i>Leaven</i> 16, no. 1 (2008), <a href="http://digitalcommons.pepperdine.edu/leaven/vol16/iss1/" id="Av87" target="_blank">http://digitalcommons.pepperdine.edu/leaven/vol16/iss1/</a>.
						</p>
</div>
</div>
</div>
</template>
<script>

export default {
  name: 'chapter',
  data() {
    return {}
    },
      components: {
    }
  }
</script>



// WEBPACK FOOTER //
// chapter02.vue?19285489